import { estimateRecipe } from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, FileClock, Plus, Save, TriangleAlert, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  keys,
  type RecipeDetail,
  send,
  useBrewSystems,
  useChange,
  useRecipe,
  useRecipeDraft,
  useRecipeDrafts,
  useRecipes,
} from "../api";
import { type SaveStatus, saveStatusText, sendAutosave, useAutosave } from "../autosave";
import {
  Dialog,
  DialogForm,
  ErrorNote,
  Field,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import { showDateTime } from "../format";
import { useKeptOnLeaving, useNotify } from "../notify";
import { RECIPE_TYPE_LABELS } from "../recipeFormat";
import { knownStyles, styleNamed } from "../recipeViews";
import { type RecipeStyle, recipeStyleOf, useStyleGuide } from "../styleGuide";
import { BatchSection, FermentationSection, LineEditor, LivePanel } from "./BuilderSections";
import { IngredientPicker } from "./IngredientPicker";
import {
  blankDraft,
  type Copy,
  copyOf,
  DRAFT_SHAPE,
  type Draft,
  type DraftLine,
  draftOf,
  editCopyKey,
  inputOf,
  type Kept,
  mathLine,
  mintId,
  missing,
  newCopyKey,
  num,
  replaceIn,
  type Start,
  settingsOf,
  shownName,
  startFrom,
  startingSystem,
} from "./recipeDraft";

export function RecipeBuilderPage() {
  usePageTitle("Recipe builder");
  const { draft: asked, from: copying } = useSearch({ from: "/recipes/builder" });
  // A new recipe's draft has its id from the start; the address takes it once there is something to
  // keep, so reloading picks up the same draft.
  const [fresh, setFresh] = useState(mintId);
  const shown = useRef<string | undefined>(asked);
  // Opened afresh from the menu while on a draft: a new recipe, not the one just left.
  useEffect(() => {
    if (shown.current && !asked) setFresh(mintId());
    shown.current = asked;
  }, [asked]);
  const draftId = asked ?? fresh;
  const { data: recipes, isLoading } = useRecipes();
  const newest = recipes?.[0]
    ? [...recipes].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
    : undefined;
  const { data: template, isLoading: loadingTemplate } = useRecipe(newest?.id ?? "");
  const { data: systems = [], isLoading: loadingSystems } = useBrewSystems();
  const { data: server, isLoading: loadingDraft } = useRecipeDraft(asked ?? null);
  const { data: original, isLoading: loadingOriginal } = useRecipe(asked ? "" : (copying ?? ""));
  // Where a draft starts is settled once, when it opens: the address taking its id, or the drafts
  // being fetched again after a save, must not start it over under the brewer's fingers.
  const opened = useRef<{ id: string; start: Start } | null>(null);
  if (opened.current?.id !== draftId) {
    if (
      isLoading ||
      loadingSystems ||
      (newest && loadingTemplate) ||
      (asked && loadingDraft) ||
      loadingOriginal
    ) {
      return <div className="page-body muted">Loading</div>;
    }
    const from = newest ? template : undefined;
    const start = startFrom(newCopyKey(draftId), server, {
      draftId,
      draft: original ? copyOf(original) : blankDraft(from, startingSystem(from, systems)),
    });
    opened.current = { id: draftId, start };
  }
  const { start } = opened.current as { start: Start };
  return <Builder key={draftId} opened={start} recipe={null} copyKey={newCopyKey(draftId)} />;
}
export function RecipeEditPage() {
  const { recipeId } = useParams({ from: "/recipes/$recipeId/edit" });
  const { data: recipe, isLoading, error } = useRecipe(recipeId);
  usePageTitle(recipe?.name, "Recipe builder");
  const { data: drafts, isLoading: loadingDrafts } = useRecipeDrafts();
  const waiting = drafts?.find((d) => d.recipe?.id === recipeId) ?? null;
  const { data: server, isLoading: loadingDraft } = useRecipeDraft(waiting?.id ?? null);
  const [fresh] = useState(mintId);
  const opened = useRef<{ id: string; start: Start } | null>(null);
  if (opened.current?.id !== recipeId) {
    if (isLoading || loadingDrafts || (waiting && loadingDraft)) {
      return <div className="page-body muted">Loading</div>;
    }
    if (error || !recipe) {
      return (
        <div className="page-body">
          <ErrorNote error={error ?? new Error("Recipe not found")} />
        </div>
      );
    }
    const start = startFrom(editCopyKey(recipe.id), server, {
      draftId: fresh,
      draft: draftOf(recipe),
    });
    opened.current = { id: recipeId, start };
  }
  const { start } = opened.current as { start: Start };
  if (!recipe) return <div className="page-body muted">Loading</div>;
  return (
    <Builder key={recipe.id} opened={start} recipe={recipe} copyKey={editCopyKey(recipe.id)} />
  );
}
/**
 * The style as typed. One from the style guide or the brewery's own recipes brings its ranges (Scott,
 * 2026-10-02); typing on past one drops them, as they belong to that style; any other style keeps what it
 * has while renamed.
 */
function styleTyped(was: Draft["style"], name: string, styles: RecipeStyle[]): Draft["style"] {
  if (!name) return null;
  const known = styleNamed(styles, name);
  if (known) return { ...known, name };
  const fromList = was ? styleNamed(styles, was.name) : undefined;
  const blank = {
    guide: null,
    code: null,
    category: null,
    og: null,
    fg: null,
    ibu: null,
    srm: null,
    abv: null,
  };
  return { ...(was && !fromList ? was : blank), name };
}
/**
 * Where Save writes, as picked in the builder. A new recipe with the name untouched is named as a copy,
 * so the list does not show two recipes by one name; going back takes the copy's name away again.
 */
function savingAs(to: string, d: Draft, from: RecipeDetail): Partial<Draft> {
  const copy = `${from.name} copy`;
  const name =
    to === "recipe" && d.name.trim() === from.name
      ? copy
      : to !== "recipe" && d.name.trim() === copy
        ? from.name
        : d.name;
  return { newVersion: to === "new", newRecipe: to === "recipe", name };
}
function Builder({
  opened,
  recipe,
  copyKey,
}: {
  opened: Start;
  recipe: RecipeDetail | null;
  copyKey: string;
}) {
  const [start] = useState(opened);
  const [draft, setDraft] = useState<Draft>(start.draft);
  const [picking, setPicking] = useState(false);
  // The line Replace is choosing an item for, by its key.
  const [replacingKey, setReplacingKey] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: systems } = useBrewSystems();
  const { draftId } = start;
  // Where Save writes: over this version, as the next one, or as a recipe of its own.
  const asRecipe = recipe !== null && draft.newRecipe === true;
  const asNew = recipe !== null && !asRecipe && draft.newVersion === true;
  const next = recipe ? Math.max(...recipe.versions.map((v) => v.version), recipe.version) + 1 : 1;
  const { data: recipes = [] } = useRecipes();
  const { data: guide = [] } = useStyleGuide();
  // The guide's styles, then any of the brewery's own the guide does not name.
  const styles = useMemo(() => {
    const fromGuide = guide.map(recipeStyleOf);
    const own = knownStyles(recipes).filter((s) => !styleNamed(fromGuide, s.name));
    return [...fromGuide, ...own];
  }, [guide, recipes]);
  const styleListId = useId();

  const autosave = useAutosave<Copy>({
    localKey: copyKey,
    save: async ({ value, savedAt }, keepalive) => {
      const kept: Kept = { v: DRAFT_SHAPE, draft: value.draft };
      const result = await sendAutosave(
        "PUT",
        `/recipe-drafts/${value.id}`,
        { recipeId: recipe?.id ?? null, name: value.draft.name.trim(), state: kept, savedAt },
        keepalive,
      );
      if (result === "saved") {
        void queryClient.invalidateQueries({ queryKey: keys.recipeDrafts, exact: true });
      }
      return result;
    },
  });
  // Sent whenever it differs from what was last sent; the page as it opened only if it never got there.
  const sent = useRef<Draft | null>(start.unsent ? null : start.draft);
  useEffect(() => {
    if (draft === sent.current) return;
    sent.current = draft;
    autosave.update({ v: DRAFT_SHAPE, id: draftId, draft });
    // A new recipe's address takes its draft the first time there is something to keep.
    if (!recipe) {
      void navigate({ to: "/recipes/builder", search: { draft: draftId }, replace: true });
    }
  }, [draft, draftId, recipe, navigate, autosave.update]);
  const hasDraft = start.resumedFrom !== null || autosave.status !== "idle";
  // Leaving with a draft says where it waits, and tapping that opens it again (Tyler, 2026-10-01).
  // Saving or throwing it away leaves nothing waiting.
  const departed = useKeptOnLeaving(() => {
    if (!hasDraft) return null;
    return recipe
      ? {
          title: "Draft kept",
          body: asRecipe
            ? `Your new recipe from ${recipe.name} is kept as a draft until you save it.`
            : asNew
              ? `Version ${next} of ${recipe.name} is kept as a draft until you save it.`
              : `Your changes to ${recipe.name} are kept as a draft until you save them.`,
          link: { kind: "recipe_draft", draftId, recipeId: recipe.id },
        }
      : {
          title: "Draft kept",
          body: `${draft.name.trim() || "Your new recipe"} is waiting under Unsaved drafts in the Recipe builder.`,
          link: { kind: "recipe_draft", draftId },
        };
  });

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setLine = (key: string, patch: Partial<DraftLine>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
    }));
  const removeLine = (key: string) =>
    setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.key !== key) }));
  const replacing = draft.lines.find((l) => l.key === replacingKey) ?? null;
  // The recipe page's guess for a line still unlinked and named as it was saved.
  const saved = replacing?.id ? recipe?.lines.find((l) => l.id === replacing.id) : undefined;
  const suggestion =
    replacing && !replacing.itemId && saved?.name === replacing.name ? saved.suggestion : null;

  const estimate = useMemo(() => {
    const s = settingsOf(draft);
    if (!Number.isFinite(s.batchLiters) || s.batchLiters <= 0) return null;
    return estimateRecipe(
      { ...s, boilMinutes: s.boilMinutes || 60, efficiencyPct: s.efficiencyPct || 0 },
      draft.lines.map(mathLine),
    );
  }, [draft]);

  const problems = [
    ...(draft.name.trim() ? [] : ["a name"]),
    ...(Number.isFinite(num(draft.batchGal)) && num(draft.batchGal) > 0 ? [] : ["a batch size"]),
    ...(Number.isFinite(num(draft.efficiencyPct)) ? [] : ["an efficiency"]),
    ...(draft.expectedAttenuationPct.trim() &&
    !(num(draft.expectedAttenuationPct) > 0 && num(draft.expectedAttenuationPct) <= 100)
      ? ["an attenuation up to 100%"]
      : []),
    ...(draft.expectedFg.trim() && !(num(draft.expectedFg) >= 0.98 && num(draft.expectedFg) <= 1.2)
      ? ["an FG like 1.010"]
      : []),
    ...(num(draft.fermentationDays) >= 1 && num(draft.fermentationDays) <= 730
      ? []
      : ["a fermentation time from 1 to 730 days"]),
    ...(num(draft.dryHopDays) >= 0 && num(draft.dryHopDays) <= 365
      ? []
      : ["a dry hop time from 0 to 365 days"]),
    ...(num(draft.lagerCrashDays) >= 0 && num(draft.lagerCrashDays) <= 365
      ? []
      : ["a lager/crash time from 0 to 365 days"]),
    ...(num(draft.briteDays) >= 0 && num(draft.briteDays) <= 365
      ? []
      : ["a brite tank time from 0 to 365 days"]),
    ...draft.lines
      .map((l) => [l, missing(l)] as const)
      .filter(([, m]) => m.length)
      .map(([l, m]) => `${shownName(l)}'s ${m.join(" and ")}`),
  ];

  const save = useChange(
    async (_: undefined, key: string) => {
      // Anything still saving lands first; the recipe written then takes the draft away with it.
      await autosave.settle();
      // A system deleted since it was picked is no longer named; the figures it gave stay.
      const gone = systems && !systems.some((s) => s.id === draft.brewSystemId);
      const body = inputOf(gone ? { ...draft, brewSystemId: null } : draft);
      try {
        const input = { body, draft: draftId };
        // A new recipe from this one takes the draft away with it, as a recipe's own save does.
        const saved =
          !recipe || asRecipe
            ? await send.createRecipe(input, key)
            : asNew
              ? await send.newRecipeVersion({ id: recipe.id, ...input }, key)
              : await send.saveRecipe({ id: recipe.id, ...input }, key);
        // Here rather than after: the page can go while everything else catches up with the save.
        departed.current = true;
        return saved;
      } catch (error) {
        // Not saved after all: the draft goes on being kept.
        autosave.update({ v: DRAFT_SHAPE, id: draftId, draft });
        throw error;
      }
    },
    {
      onSuccess: (saved) => {
        notify({
          title: asRecipe
            ? `${saved.name} saved as a new recipe`
            : asNew
              ? `Version ${saved.version} of ${saved.name} saved`
              : `${saved.name} saved`,
          link: { kind: "recipe", recipeId: saved.id },
        });
        navigate({ to: "/recipes/$recipeId", params: { recipeId: saved.id } });
      },
    },
  );
  const discard = useChange(
    async (_: undefined, key: string) => {
      await autosave.settle();
      const thrown = await send.discardRecipeDraft(draftId, key);
      departed.current = true;
      return thrown;
    },
    {
      onSuccess: () => {
        setDiscarding(false);
        notify(recipe ? "Changes thrown away" : "Draft thrown away");
        if (recipe) navigate({ to: "/recipes/$recipeId", params: { recipeId: recipe.id } });
        else navigate({ to: "/recipes/builder", search: {} });
      },
    },
  );
  const submit = () => {
    setTried(true);
    if (problems.length === 0) save.mutate(undefined);
  };
  const backTo = recipe?.id ?? draft.copiedFrom?.id;

  return (
    <>
      <Toolbar
        left={
          <>
            {backTo ? (
              <Link
                to="/recipes/$recipeId"
                params={{ recipeId: backTo }}
                className="toolbar-button"
              >
                <ArrowLeft size={20} />
                <span className="label">Back</span>
              </Link>
            ) : (
              <Link to="/recipes" className="toolbar-button">
                <ArrowLeft size={20} />
                <span className="label">Recipes</span>
              </Link>
            )}
            <ToolbarSeparator />
            <ToolbarButton
              icon={<Plus size={20} />}
              label="Ingredient"
              title="Add an ingredient"
              onClick={() => setPicking(true)}
            />
          </>
        }
        right={
          <>
            {hasDraft ? (
              <ToolbarButton
                icon={<X size={20} />}
                label="Discard"
                title={recipe ? "Throw these changes away" : "Throw this draft away"}
                onClick={() => setDiscarding(true)}
              />
            ) : null}
            <ToolbarButton
              icon={<Save size={20} />}
              label={
                !recipe
                  ? "Save"
                  : asRecipe
                    ? "Save as new recipe"
                    : `Save v${asNew ? next : recipe.version}`
              }
              title={
                !recipe
                  ? "Save the recipe"
                  : asRecipe
                    ? `Save as a new recipe, leaving ${recipe.name} as it is`
                    : asNew
                      ? `Save as version ${next}, leaving version ${recipe.version} as it is`
                      : "Save over this version"
              }
              disabled={save.isPending}
              onClick={submit}
            />
          </>
        }
      />
      <div className="page-body builder">
        <div className="builder-main">
          <div className="builder-title">
            <h1 className="page-title">
              {!recipe
                ? draft.copiedFrom
                  ? `New recipe from ${draft.copiedFrom.name}`
                  : "New recipe"
                : asRecipe
                  ? `New recipe from ${recipe.name}`
                  : asNew
                    ? `New version of ${recipe.name}`
                    : `Editing ${recipe.name}`}
            </h1>
            <DraftState status={autosave.status} editing={recipe !== null} />
          </div>
          {start.resumedFrom ? (
            <p className="notice builder-resumed">
              <FileClock size={16} />{" "}
              {recipe ? "Picked up your unsaved changes" : "Picked up your draft"} from{" "}
              {showDateTime(start.resumedFrom)}.
            </p>
          ) : null}
          {recipe ? null : <OtherDrafts current={draftId} />}
          {tried && problems.length ? (
            <div className="form-error">Still needs {problems.join(", ")}.</div>
          ) : null}
          <ErrorNote error={save.error} />
          <section className="builder-card">
            <div className="form-grid">
              <Field label="Name" className="wide">
                <input
                  className="input"
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                />
              </Field>
              <Field label="Brewer">
                <input
                  className="input"
                  value={draft.brewer}
                  onChange={(e) => set({ brewer: e.target.value })}
                />
              </Field>
              <Field label="Type">
                <select
                  className="select"
                  value={draft.type}
                  onChange={(e) => set({ type: e.target.value as Draft["type"] })}
                >
                  {Object.entries(RECIPE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Style"
                hint={
                  draft.style?.guide
                    ? `${draft.style.guide}, with its ranges`
                    : "Type to pick from the GABF guidelines, with their ranges"
                }
              >
                <input
                  className="input"
                  aria-label="Style"
                  list={styleListId}
                  value={draft.style?.name ?? ""}
                  onChange={(e) => set({ style: styleTyped(draft.style, e.target.value, styles) })}
                />
                <datalist id={styleListId}>
                  {styles.map((s) => (
                    <option
                      key={s.name}
                      value={s.name}
                      label={s.guide ? `${s.guide}, ${s.code}` : "From your recipes"}
                    />
                  ))}
                </datalist>
              </Field>
              {recipe ? (
                <Field
                  label="Save as"
                  hint={
                    asRecipe
                      ? `${recipe.name} stays as it is`
                      : asNew
                        ? `Version ${recipe.version} stays as it is`
                        : `Changes version ${recipe.version}`
                  }
                >
                  <select
                    className="select"
                    value={asRecipe ? "recipe" : asNew ? "new" : "this"}
                    onChange={(e) => set(savingAs(e.target.value, draft, recipe))}
                  >
                    <option value="this">Version {recipe.version}, this one</option>
                    <option value="new">Version {next}, a new version</option>
                    <option value="recipe">A new recipe</option>
                  </select>
                </Field>
              ) : (
                <Field label="Version">
                  <input className="input" value={1} disabled />
                </Field>
              )}
              {/* No description field: Scott does not use one (Jam, 2026-10-02). One a recipe already
                  has is kept as it is, and still shows on its card. */}
            </div>
          </section>

          <BatchSection draft={draft} set={set} />

          <FermentationSection draft={draft} set={set} estimate={estimate} />

          <section className="builder-card">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2>Ingredients</h2>
              <button
                type="button"
                className="button primary small"
                onClick={() => setPicking(true)}
              >
                <Plus size={14} /> Add ingredient
              </button>
            </div>
            {draft.lines.length === 0 ? (
              <p className="muted">
                Nothing yet. Add what is on hand first, or anything from the catalog.
              </p>
            ) : (
              <div className="builder-lines">
                {draft.lines.map((l, i) => (
                  <LineEditor
                    key={l.key}
                    line={l}
                    estimate={estimate?.lines[i]}
                    flagged={tried && missing(l).length > 0}
                    onChange={(patch) => setLine(l.key, patch)}
                    onReplace={() => setReplacingKey(l.key)}
                    onRemove={() => removeLine(l.key)}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="builder-card">
            <h2>Notes</h2>
            <textarea
              className="input"
              rows={4}
              aria-label="Notes"
              value={draft.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </section>
        </div>
        <LivePanel draft={draft} estimate={estimate} />
      </div>
      <IngredientPicker
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(line) => {
          setDraft((d) => ({ ...d, lines: [...d.lines, line] }));
          setPicking(false);
        }}
      />
      {replacing ? (
        <IngredientPicker
          open
          replacing={replacing}
          suggestion={suggestion}
          onClose={() => setReplacingKey(null)}
          onPick={(made) => {
            setDraft((d) => ({ ...d, lines: replaceIn(d.lines, replacing.key, made) }));
            setReplacingKey(null);
          }}
        />
      ) : null}
      <Dialog
        open={discarding}
        onClose={() => setDiscarding(false)}
        title={recipe ? "Throw these changes away?" : "Throw this draft away?"}
      >
        <DialogForm
          onSubmit={() => discard.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setDiscarding(false)}>
                Keep it
              </button>
              <button type="submit" className="button danger" disabled={discard.isPending}>
                Throw away
              </button>
            </>
          }
        >
          <p>
            {recipe
              ? `${recipe.name} stays as it was last saved.`
              : "It has not been saved as a recipe, so it is gone for good."}
          </p>
          <ErrorNote error={discard.error} />
        </DialogForm>
      </Dialog>
    </>
  );
}
/** Where the draft is: the builder's own wording of the autosave note. */
function DraftState({ status, editing }: { status: SaveStatus; editing: boolean }) {
  const text =
    status === "saved"
      ? editing
        ? "Changes kept as a draft until you save"
        : "Draft saved"
      : saveStatusText(status, "A draft saves as you type");
  return (
    <span className={`builder-save ${status}`} role="status">
      {status === "retrying" ? <TriangleAlert size={14} /> : null}
      {text}
    </span>
  );
}
/** The builder's other drafts, to pick up where they were left. */
function OtherDrafts({ current }: { current: string }) {
  const { data: drafts = [] } = useRecipeDrafts();
  const others = drafts.filter((d) => d.id !== current);
  if (others.length === 0) return null;
  return (
    <section className="builder-drafts" aria-label="Unsaved drafts">
      <h2>Unsaved drafts</h2>
      {others.map((d) =>
        d.recipe ? (
          <Link
            key={d.id}
            to="/recipes/$recipeId/edit"
            params={{ recipeId: d.recipe.id }}
            className="builder-draft"
          >
            <strong>{d.recipe.name}</strong>
            <span className="muted">
              Changes to version {d.recipe.version}, {showDateTime(d.savedAt)}
            </span>
          </Link>
        ) : (
          <Link key={d.id} to="/recipes/builder" search={{ draft: d.id }} className="builder-draft">
            <strong>{d.name || "Untitled recipe"}</strong>
            <span className="muted">New recipe, {showDateTime(d.savedAt)}</span>
          </Link>
        ),
      )}
    </section>
  );
}
